import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import logo from '../img/logo.png';
import {
    Menu,
    X,
    Shield,
    Mail,
    Phone,
    MapPin,
    Send,
    Globe,
    ChevronDown
} from 'lucide-react';
import { contactService, ContactFormData } from '../services/contactService';
import icon from "../img/icon.png";
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../hooks/useLanguage';

const ContactPage: React.FC = () => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string }>({
        success: false,
        message: ''
    });

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = t('contact-page.form.validation.nameRequired');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('contact-page.form.validation.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('contact-page.form.validation.emailInvalid');
        }

        if (!formData.subject.trim()) {
            newErrors.subject = t('contact-page.form.validation.subjectRequired');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('contact-page.form.validation.messageRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            await contactService.sendContactEmail(formData);
            setSubmitStatus({
                success: true,
                message: t('contact-page.form.success')
            });
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            setSubmitStatus({
                success: false,
                message: t('contact-page.form.error')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : ''}`}>
            {/* Navigation */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo et nom */}
                        <div className="flex-shrink-0 flex items-center">
                            <Link to="/">
                                <img src={logo} alt="Hirrif Logo" width={80} height={80} />
                            </Link>
                        </div>

                        {/* Menu desktop */}
                        <div className="hidden md:flex items-center space-x-8">
                            <Link to="/#comment-ca-marche" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.howItWorks')}
                            </Link>
                            <Link to="/#services" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.services')}
                            </Link>
                            <Link to="/#tarifs" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.pricing')}
                            </Link>
                            <Link to="/contact" className="text-[#115DF2] font-medium">
                                {t('nav.contact')}
                            </Link>
                            <LanguageSelector />
                            <Link to="/login" className="text-[#115DF2] hover:text-[#0E4BC2] font-medium">
                                {t('nav.login')}
                            </Link>
                        </div>

                        {/* Menu mobile burger */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                                type="button"
                                aria-label="Menu"
                            >
                                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </button>
                        </div>
                    </div>

                    {/* Menu mobile panel */}
                    {isMenuOpen && (
                        <div className="md:hidden bg-white border-t">
                            <div className="px-2 pt-2 pb-3 space-y-1">
                                <Link
                                    to="/#comment-ca-marche"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.howItWorks')}
                                </Link>
                                <Link
                                    to="/#services"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.services')}
                                </Link>
                                <Link
                                    to="/#tarifs"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.pricing')}
                                </Link>
                                <Link
                                    to="/contact"
                                    className="block px-3 py-2 rounded-lg text-[#115DF2] bg-[rgba(17,93,242,0.05)]"
                                >
                                    {t('nav.contact')}
                                </Link>
                                <div className="px-3 py-2">
                                    <LanguageSelector />
                                </div>
                                <Link
                                    to="/login"
                                    className="block px-3 py-2 rounded-lg text-[#115DF2] hover:bg-[rgba(17,93,242,0.05)]"
                                >
                                    {t('nav.login')}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Page Header */}
            <div className="pt-16 pb-12 bg-[rgba(17,93,242,0.05)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
                            {t('contact-page.header.title')}
                        </h1>
                        <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
                            {t('contact-page.header.subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Contact Section */}
            <div className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Info section */}
                        <div className="lg:col-span-1">
                            <div className="bg-white p-6 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold mb-6">
                                    {t('contact-page.info.title')}
                                </h3>

                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0 mt-1">
                                            <Mail className="h-6 w-6 text-[#115DF2]" />
                                        </div>
                                        <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                                            <p className="text-sm font-medium text-gray-900">
                                                {t('contact-page.info.email')}
                                            </p>
                                            <a href="mailto:contact@inspecly.io" className="text-sm text-gray-500 hover:text-[#115DF2]">
                                                contact@inspecly.io
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start">
                                        <div className="flex-shrink-0 mt-1">
                                            <MapPin className="h-6 w-6 text-[#115DF2]" />
                                        </div>
                                        <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                                            <p className="text-sm font-medium text-gray-900">
                                                {t('contact-page.info.address')}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {t('footer.address')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form section */}
                        <div className="lg:col-span-2">
                            <div className="bg-white p-6 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold mb-6">
                                    {t('contact-page.form.title')}
                                </h3>

                                {submitStatus.message && (
                                    <div className={`p-4 mb-6 rounded-lg ${submitStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                        {submitStatus.message}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                                {t('contact-page.form.labels.name')}
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#115DF2] focus:border-[#115DF2] ${isRTL ? 'text-right' : 'text-left'}`}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                                        </div>

                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                                {t('contact-page.form.labels.email')}
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                id="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#115DF2] focus:border-[#115DF2] ${isRTL ? 'text-right' : 'text-left'}`}
                                                dir="ltr"
                                            />
                                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                                            {t('contact-page.form.labels.subject')}
                                        </label>
                                        <input
                                            type="text"
                                            name="subject"
                                            id="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className={`mt-1 block w-full px-3 py-2 border ${errors.subject ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#115DF2] focus:border-[#115DF2] ${isRTL ? 'text-right' : 'text-left'}`}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                                            {t('contact-page.form.labels.message')}
                                        </label>
                                        <textarea
                                            name="message"
                                            id="message"
                                            rows={6}
                                            value={formData.message}
                                            onChange={handleChange}
                                            className={`mt-1 block w-full px-3 py-2 border ${errors.message ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#115DF2] focus:border-[#115DF2] ${isRTL ? 'text-right' : 'text-left'}`}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                                    </div>

                                    <div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-[#115DF2] hover:bg-[#0E4BC2] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#115DF2] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        >
                                            {isSubmitting ? t('contact-page.form.submitting') : t('contact-page.form.submit')}
                                            <Send className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-[#0A337F] text-white">
                <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="md:col-span-1">
                            <div className="flex items-center">
                                <img src={icon} alt="Hirrif Logo" width={30} height={30}  />
                                <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-2xl font-bold`}>
                                    Inspecly<span className="text-[#9DB5F3] font-normal">.io</span>
                                </span>
                            </div>
                            <p className="mt-4 text-[#C5D3F9]">
                                {t('footer.description')}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t('footer.legal')}</h3>
                            <ul className="space-y-2">
                                <li>
                                    <Link to="/cgu" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.terms')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/cgv" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.sales')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/privacy-policy" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.privacy')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/cookie-policy" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.cookies')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
                            <p className="text-[#C5D3F9]">
                                {t('footer.email')}<br />
                                {t('footer.address')}
                            </p>
                            <div className="mt-4 flex space-x-4">
                                <a
                                    href="#"
                                    className="text-[#C5D3F9] hover:text-white"
                                    title="Télécharger sur App Store"
                                >
                                    <svg viewBox="0 0 384 512" className="w-6 h-6">
                                        <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                                    </svg>
                                </a>
                                <a
                                    href="#"
                                    className="text-[#C5D3F9] hover:text-white"
                                    title="Télécharger sur Google Play"
                                >
                                    <svg viewBox="0 0 512 512" className="w-6 h-6">
                                        <path fill="currentColor" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 pt-8 border-t border-[#1A4395] text-center text-[#C5D3F9]">
                        <p>&copy; {new Date().getFullYear()} BIYA IT SAS. {t('footer.copyright')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ContactPage;